import React, { useRef, useState, useEffect } from 'react';
import SectionTitle from './SectionTitle';

// Client logos data - each logo gets a uniform container regardless of original size
// needsDarkBg: true for logos with white/light content on transparent background
export const trustedClients = [
    {
        id: 1,
        name: 'Kanchan India Ltd.',
        logo: '/images/clients/kanchan-india.jpg'
    },
    {
        id: 2,
        name: 'Narmada',
        logo: '/images/clients/narmada.png'
    },
    {
        id: 3,
        name: 'Parul University',
        logo: '/images/clients/parul-university.jpg'
    },
    {
        id: 4,
        name: 'Bhilwara Group',
        logo: '/images/clients/bhilwara-group.png'
    },
    {
        id: 5,
        name: 'Gokul Global University',
        logo: '/images/clients/gokul-university.png'
    },
    {
        id: 6,
        name: 'Parul University Goa',
        logo: '/images/clients/parul-university-goa.png'
    },
    {
        id: 7,
        name: 'Sangam India Limited',
        logo: '/images/clients/sangam-india.jpg'
    },
    {
        id: 8,
        name: 'Shaktiman Grimme',
        logo: '/images/clients/shaktiman-grimme.png'
    },
    {
        id: 9,
        name: 'Shaktiman',
        logo: '/images/clients/shaktiman.png'
    },
    {
        id: 10,
        name: 'Skretting',
        logo: '/images/clients/skretting.png'
    },
    {
        id: 11,
        name: 'Virat',
        logo: '/images/clients/virat.png'
    },
    {
        id: 12,
        name: 'VNC Robotics & Instruments',
        logo: '/images/clients/vnc-robotics.jpg'
    }
];

// Single client card component
const ClientCard = ({ client, isDark, variant = 'scroll' }) => (
    <div className={`group relative ${variant === 'scroll' ? 'flex-shrink-0' : 'w-full'}`}>
        {/* Card with fixed aspect ratio container */}
        <div className={`
            relative overflow-hidden rounded-xl
            ${variant === 'scroll' ? 'w-40 sm:w-48 md:w-56' : 'w-full'}
            ${isDark
                ? 'bg-white/10 backdrop-blur-sm border border-white/20 hover:border-yellow-500/50'
                : 'bg-white border border-gray-100 shadow-lg hover:shadow-xl hover:border-yellow-200'
            }
            transition-all duration-500 transform hover:scale-105
        `}>
            {/* Aspect ratio container - maintains consistent height */}
            <div className="aspect-[3/2] p-3 sm:p-4 md:p-6 flex items-center justify-center">
                {/* Image wrapper with object-contain for proper sizing */}
                <img
                    src={client.logo}
                    alt={client.name}
                    title={client.name}
                    className="max-w-full max-h-full object-contain transition-all duration-500 opacity-80 group-hover:opacity-100"
                    loading="lazy"
                />
            </div>

            {/* Subtle hover glow effect */}
            <div className={`
                absolute inset-0 opacity-0 group-hover:opacity-100 
                transition-opacity duration-500 pointer-events-none
                bg-gradient-to-t ${isDark ? 'from-yellow-500/10' : 'from-yellow-500/5'} to-transparent
            `}></div>
        </div>

        {/* Client name tooltip on hover */}
        <div className={`
            absolute -bottom-2 left-1/2 transform -translate-x-1/2 translate-y-full
            px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium whitespace-nowrap
            opacity-0 group-hover:opacity-100 group-hover:-translate-y-0
            transition-all duration-300 pointer-events-none z-10
            ${isDark
                ? 'bg-yellow-500 text-gray-900'
                : 'bg-gray-900 text-white'
            }
        `}>
            {client.name}
            {/* Arrow */}
            <div className={`
                absolute -top-1 left-1/2 transform -translate-x-1/2 
                border-4 border-transparent 
                ${isDark ? 'border-b-yellow-500' : 'border-b-gray-900'}
            `}></div>
        </div>
    </div>
);

const TrustedClients = ({
    theme = 'light',
    variant = 'scroll', // 'scroll' for homepage, 'grid' for dedicated page
    showStats = true,
    compact = false
}) => {
    const isDark = theme === 'dark';
    const scrollContainerRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(true);

    const checkScrollButtons = () => {
        const container = scrollContainerRef.current;
        if (container) {
            setCanScrollLeft(container.scrollLeft > 0);
            setCanScrollRight(
                container.scrollLeft < container.scrollWidth - container.clientWidth - 10
            );
        }
    };

    useEffect(() => {
        checkScrollButtons();
        const container = scrollContainerRef.current;
        if (container) {
            container.addEventListener('scroll', checkScrollButtons);
            window.addEventListener('resize', checkScrollButtons);
        }
        return () => {
            if (container) {
                container.removeEventListener('scroll', checkScrollButtons);
            }
            window.removeEventListener('resize', checkScrollButtons);
        };
    }, []);

    const scroll = (direction) => {
        const container = scrollContainerRef.current;
        if (container) {
            const scrollAmount = 300;
            container.scrollBy({
                left: direction === 'left' ? -scrollAmount : scrollAmount,
                behavior: 'smooth'
            });
        }
    };

    return (
        <section className={`${compact ? 'py-12' : 'py-20'} ${isDark ? 'bg-gray-800' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
            <div className="container mx-auto px-4">
                <SectionTitle
                    subtitle="Our Network"
                    title="Our Trusted Clients"
                    description={compact ? null : "We are proud to partner with industry leaders who trust us for their critical needs"}
                    theme={theme}
                    className={compact ? 'mb-8' : ''}
                />

                {variant === 'scroll' ? (
                    /* Horizontal Scroll Layout */
                    <div className="relative">
                        {/* Left scroll button */}
                        <button
                            onClick={() => scroll('left')}
                            className={`
                                absolute left-0 top-1/2 -translate-y-1/2 z-10
                                w-12 h-12 rounded-full shadow-lg
                                flex items-center justify-center
                                transition-all duration-300
                                ${canScrollLeft
                                    ? 'opacity-100 cursor-pointer'
                                    : 'opacity-0 cursor-default pointer-events-none'
                                }
                                ${isDark
                                    ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900'
                                    : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-200'
                                }
                            `}
                            aria-label="Scroll left"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>

                        {/* Scrollable container */}
                        <div
                            ref={scrollContainerRef}
                            className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 px-4 -mx-4 scroll-smooth"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {trustedClients.map((client) => (
                                <ClientCard key={client.id} client={client} isDark={isDark} variant="scroll" />
                            ))}
                        </div>

                        {/* Right scroll button */}
                        <button
                            onClick={() => scroll('right')}
                            className={`
                                absolute right-0 top-1/2 -translate-y-1/2 z-10
                                w-12 h-12 rounded-full shadow-lg
                                flex items-center justify-center
                                transition-all duration-300
                                ${canScrollRight
                                    ? 'opacity-100 cursor-pointer'
                                    : 'opacity-0 cursor-default pointer-events-none'
                                }
                                ${isDark
                                    ? 'bg-yellow-500 hover:bg-yellow-400 text-gray-900'
                                    : 'bg-white hover:bg-gray-50 text-gray-800 border border-gray-200'
                                }
                            `}
                            aria-label="Scroll right"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                            </svg>
                        </button>

                        {/* Gradient fade edges - extended beyond padding */}
                        <div className={`absolute -left-4 top-0 bottom-4 w-20 pointer-events-none bg-gradient-to-r ${isDark ? 'from-gray-800' : 'from-gray-50'} to-transparent`}></div>
                        <div className={`absolute -right-4 top-0 bottom-4 w-20 pointer-events-none bg-gradient-to-l ${isDark ? 'from-gray-800' : 'from-gray-100'} to-transparent`}></div>
                    </div>
                ) : (
                    /* Grid Layout for dedicated page */
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8">
                        {trustedClients.map((client) => (
                            <div key={client.id} className="flex justify-center">
                                <ClientCard client={client} isDark={isDark} variant="grid" />
                            </div>
                        ))}
                    </div>
                )}

                {/* Trust indicators */}
                {showStats && (
                    <div className={`${compact ? 'mt-10' : 'mt-16'} flex flex-wrap justify-center items-center gap-8 md:gap-16`}>
                        <div className="text-center">
                            <div className={`text-4xl md:text-5xl font-bold ${isDark ? 'text-yellow-500' : 'text-yellow-600'}`}>
                                12+
                            </div>
                            <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Enterprise Partners
                            </div>
                        </div>
                        <div className={`h-12 w-px ${isDark ? 'bg-gray-600' : 'bg-gray-300'} hidden md:block`}></div>
                        <div className="text-center">
                            <div className={`text-4xl md:text-5xl font-bold ${isDark ? 'text-yellow-500' : 'text-yellow-600'}`}>
                                10+
                            </div>
                            <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Years of Trust
                            </div>
                        </div>
                        <div className={`h-12 w-px ${isDark ? 'bg-gray-600' : 'bg-gray-300'} hidden md:block`}></div>
                        <div className="text-center">
                            <div className={`text-4xl md:text-5xl font-bold ${isDark ? 'text-yellow-500' : 'text-yellow-600'}`}>
                                100%
                            </div>
                            <div className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Client Satisfaction
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default TrustedClients;
